import { getId } from 'utils';

import type { ModalityT } from './modalities';
import type { PublicUserDataT, UserT } from './users';
import { type XmlNodeArrayT, type XmlNodeT, xmlNode } from './xml';

export const ARTIST_STATUS_VALUES = [
  'Active',
  'Inactive',
  'Incomplete Application',
  'Pending Verification',
  'Deleted',
] as const;

export type ArtistStatusT = typeof ARTIST_STATUS_VALUES[number];

/** Represents a single content creator. */
export type ArtistT = {
  certificationsAndTraining?: null | string[];
  createdAt?: null | number;
  customModalities?: null | string[];

  // TODO: Originally, the artist name was (and still is) available from its
  // .user.firstName, and .user.lastName fields (which were intended as public
  // user names); however, this .displayName field was introduced at some point...
  // it might be worth to revise around the entire codebase where and when
  // different source of the user name are used - I am pretty sure, it is not
  // consistent around the code.
  /** Display name for the practitioner. */
  displayName?: null | string;

  /** Facebook profile URL or handle. */
  facebook?: null | string;

  /** Object ID in the database. */
  id?: string;

  /** Instagram profile URL or handle. */
  instagram?: null | string;

  /** LinkedIn profile URL or handle. */
  linkedIn?: null | string;

  /** Taken in the application form. */
  // TODO: Remove (replaced by otherLinks below).
  linkOther?: null | string;

  /** Taken in the application form. */
  linkSocialMedia?: null | string;

  modalities?: ModalityT[] | null;

  /** Other links related to the artist. */
  otherLinks?: null | string[];

  practiceExperienceYears?: null | number;

  /** Whether the practitioner has published their page (made it publicly visible). */
  published?: boolean | null;

  /** Artist rating in the [1,5] scale. */
  rating?: null | number;

  // TODO: THIS IS TO BE REMOVED.
  sampleContent?: null | string[];

  /** Whether to show Facebook on the public page. */
  showFacebookOnPage?: boolean | null;

  /** Whether to show Instagram on the public page. */
  showInstagramOnPage?: boolean | null;

  /** Whether to show LinkedIn on the public page. */
  showLinkedInOnPage?: boolean | null;

  /** Which other links to show on the public page. */
  showOtherLinksOnPage?: boolean[] | null;

  /** Whether to show TikTok on the public page. */
  showTikTokOnPage?: boolean | null;

  /** Whether to show Website on the public page. */
  showWebsiteOnPage?: boolean | null;

  /** Whether to show X (Twitter) on the public page. */
  showXTwitterOnPage?: boolean | null;

  /** Whether to show YouTube on the public page. */
  showYoutubeOnPage?: boolean | null;

  /** Artist's specialization. */
  /** @deprecated I guess, it is to be replaced by `modalities`? */
  specialization: string;

  specialityAreas?: null | string[];

  /** Short introduction for the practitioner (max 200 words). */
  shortIntro?: null | string;

  /** Artist's summary line (one line). */
  summaryLine?: null | string;

  /** Artist's bio (up to 2 paragraphs). */
  bio?: null | string;

  /** Artist's headline quote. */
  headlineQuote?: null | string;

  /** Artist's background story. */
  background?: null | string;

  /** Artist's approach. */
  approach?: null | string;

  /** Artist status. */
  status: ArtistStatusT;

  /**
     * Captures the result of "Yes" / "No" answer to the following question in
     * the practitioner application flow "Soul Sanctuary focuses on 2-8 minute
     * segments. Are you comfortable with creating content within these
     * constraints?"
     */
  teachingAndContentCreationConsent?: boolean | null;

  termsAndConditionsAccepted?: boolean | null;

  teachingExperienceYears?: null | number;
  tikTok?: null | string;

  /** Public details from the artist's user record. */
  user: Partial<UserT> & PublicUserDataT;

  /**
     * "true" if the artist presentation video is uploaded and ready to be played;
     * "false" otherwise.
     */
  videoIsReadyToBePlayed?: boolean | null;

  /** URL of the thumbnail for the artist presentation video, if any. */
  videoThumbnailUrl?: null | string;

  /** URL of the artist presentation video, if any. */
  videoUrl?: null | string;

  /**
     * URLs of additional video files uploaded by practitioner during
     * the application flow.
     */
  videos?: null | string[];

  website?: null | string;

  /** X (Twitter) profile URL or handle. */
  xTwitter?: null | string;

  /** YouTube channel URL. */
  youtube?: null | string;
};

export const ARTIST_SELF_EDITABLE_FIELDS = [
  'approach',
  'background',
  'bio',
  'certificationsAndTraining',
  'customModalities',
  'displayName',
  'facebook',
  'headlineQuote',
  'instagram',
  'linkOther',
  'linkSocialMedia',
  'linkedIn',
  'modalities',
  'otherLinks',
  'practiceExperienceYears',
  'published',
  'sampleContent',
  'shortIntro',
  'summaryLine',
  'showFacebookOnPage',
  'showInstagramOnPage',
  'showLinkedInOnPage',
  'showOtherLinksOnPage',
  'showTikTokOnPage',
  'showWebsiteOnPage',
  'showXTwitterOnPage',
  'showYoutubeOnPage',
  'specialityAreas',
  'teachingAndContentCreationConsent',
  'teachingExperienceYears',
  'termsAndConditionsAccepted',
  'tikTok',
  'videos',
  'website',
  'xTwitter',
  'youtube',
] as const satisfies Array<keyof ArtistT>;

export const artistSelfEditableFieldSet = new Set(ARTIST_SELF_EDITABLE_FIELDS);

export type ArtistSelfEditableFieldT
  = typeof ARTIST_SELF_EDITABLE_FIELDS[number];

export type ArtistSelfUpdateT = Partial<
  Pick<ArtistT, ArtistSelfEditableFieldT>
>;

export const DEFAULT_ARTIST_PAGE_SIZE = 20;

// TODO: Later react-global-state will provide necessary global state segment
// type, and useAsyncList() hook, modelled exactly after the pattern used in
// this app, with appropriate further generalizations.
export type ArtistListT = {
  hasMore: boolean | undefined;
  items: ArtistT[];
  lastPage: number;
  numPages: number | undefined;
  pageSize: number;

  // TODO: Perhaps unnecessary? We can check if there are more than one
  // page loaded into the state by comparing items.length to pageSize -
  // that should be enough for all use cases of `singlePage`.
  singlePage: boolean;

  // TODO: Is it enough to just turn into queryString? As we just want to
  // compare new query and the old query by match, and reload old content
  // if they don't match - perhaps no need to compare original queries?
  published?: boolean;
  status: ArtistStatusT | ArtistStatusT[] | undefined;

  textSearch: string | undefined;
};

/**
 * The artist's name as shown to the public: "displayName", falling back to
 * the first/last names on the corresponding user record, and to "Unknown" if
 * none are set.
 */
export function artistFullName(artist: ArtistT): string {
  return (artist.displayName ?? '') || (
    artist.user.firstName && artist.user.lastName
      ? `${artist.user.firstName} ${artist.user.lastName}`
      : artist.user.firstName || artist.user.lastName || 'Unknown'
  );
}

/**
 * Note: the primary purpose of this artist object > XML string conversion,
 * at least for now, is to integrate artists information into the public
 * check-in, and other AI-driven conversation, thus it should respect
 * artist settings for what should be displayed to public, and what should not be.
 */
// eslint-disable-next-line complexity
export function xmlArtist(artist: ArtistT): XmlNodeT<'practitioner'> {
  const fullName = artistFullName(artist);

  const nodes: XmlNodeArrayT = [
    xmlNode('id', getId(artist), 'Internal ID.'),
    xmlNode('full_name', fullName, 'Full name.'),
    xmlNode(
      'modalities',
      ([] as string[]).concat(
        artist.modalities ?? [],
        artist.customModalities ?? [],
        artist.specialityAreas ?? [],
      ).join('; '),
      'Wellbeing modalities / specialization.',
    ),
  ];

  let info = '';

  if (artist.summaryLine) {
    info += `
# Summary
${artist.summaryLine}`;
  }

  if (artist.shortIntro) {
    info += `
# Short Intro
${artist.shortIntro}`;
  }

  if (artist.bio) {
    info += `
# Biography
${artist.bio}`;
  }

  if (artist.background) {
    info += `
# Background
${artist.background}`;
  }

  if (artist.approach) {
    info += `
# Approach
${artist.approach}`;
  }

  nodes.push(
    xmlNode(
      'information',
      info,
      'Biography, and other information about this practitioner.',
    ),
  );

  if (artist.user.avatarUrl) {
    nodes.push(xmlNode('avatar_url', artist.user.avatarUrl, 'Avatar URL.'));
  }

  if (artist.facebook && artist.showFacebookOnPage) {
    nodes.push(xmlNode('facebook', artist.facebook, 'Facebook profile URL'));
  }

  if (artist.instagram && artist.showInstagramOnPage) {
    nodes.push(xmlNode('instagram', artist.instagram, 'Instagram handle'));
  }

  if (artist.linkedIn && artist.showLinkedInOnPage) {
    nodes.push(xmlNode('linkedin', artist.linkedIn, 'LinkedIn URL'));
  }

  if (artist.otherLinks?.length && artist.showOtherLinksOnPage) {
    nodes.push(
      xmlNode(
        'other_links',
        artist.otherLinks.join('; '),
        'Other artist-related URLs.',
      ),
    );
  }

  if (artist.tikTok && artist.showTikTokOnPage) {
    nodes.push(xmlNode('tiktok', artist.tikTok, 'TikTok URL or handle.'));
  }

  if (artist.website && artist.showWebsiteOnPage) {
    nodes.push(xmlNode('website_url', artist.website, 'Website URL.'));
  }

  if (artist.xTwitter && artist.showXTwitterOnPage) {
    nodes.push(
      xmlNode('x_twitter', artist.xTwitter, 'X (Twitter) URL or handle.'),
    );
  }

  if (artist.youtube && artist.showYoutubeOnPage) {
    nodes.push(xmlNode('youtube', artist.youtube, 'YouTube URL or handle.'));
  }

  if (artist.videoUrl) {
    nodes.push(
      xmlNode(
        'video_presentation_url',
        artist.videoUrl,
        'Practitioner\'s video presentation URL.',
      ),
    );
  }

  return xmlNode(
    'practitioner',
    nodes,
    'Wellbeing practitioner (instructor, artist, coach) profile.',
  );
}
